import { useMemo } from 'react';
import { TableCell, TableRow } from '@mui/material';
import { GridLayout } from '../../../shared/ui/grid-layout';
import { useSummary } from '../../../entities/salary/salary-entries/salary-entries.hook';
import { useSearchParams } from 'react-router-dom';
import { useUsersList } from '../../../entities/user/users-list/users-list.hook';
import { SALARY_ENTRY_TYPES, SalarySummaryDto } from '@sr/dto';
import { TotalsRow } from './totals-row';
import PriceSpan from '../../../shared/ui/price-span';
import { SUMMARY_COLUMNS, useSummaryTableOptions } from './use-summary-table-options';

type SalarySummaryRow = SalarySummaryDto & {
  userId: number;
  firstName: string;
  secondName: string;
  isFired?: boolean | number;
  isActive?: boolean | number;
};

export const SummaryTable = () => {
  const [searchParams] = useSearchParams();
  const year = searchParams.get('year') ?? new Date().getFullYear();
  const month = searchParams.get('month') ?? new Date().getMonth();

  const { columns, getSpanSize, getRowTotalColor, getPriceColor, paging } = useSummaryTableOptions();
  const { rows, error, isLoading: summaryIsLoading } = useSummary(Number(year), Number(month));
  const { collection, isLoading: userIsLoading, error: userError } = useUsersList(paging);

  const data = useMemo(() => {
    const total: Record<SALARY_ENTRY_TYPES | 'total', number> = Object.values(SALARY_ENTRY_TYPES).reduce(
      (acc, key) => ({ ...acc, [key]: 0 }),
      {} as Record<SALARY_ENTRY_TYPES | 'total', number>,
    );
    total.total = 0;

    const users = collection.items ?? [];
    const usersMap = new Map(users.map((u) => [u.id, u]));

    const buildRow = (
      userId: number,
      firstName: string,
      secondName: string,
      sum: any,
      isFired?: boolean | number,
      isActive?: boolean | number,
    ): SalarySummaryRow => {

      const items: Record<SALARY_ENTRY_TYPES, number> = {} as Record<SALARY_ENTRY_TYPES, number>;
      SUMMARY_COLUMNS.forEach(({ key }) => {
        items[key] = Number(sum?.[key] ?? 0);
        total[key] = (Math.round(total[key] * 100) + Math.round(items[key] * 100)) / 100;
      });

      const rowTotal = sum?.total ?? 0;
      total.total += rowTotal;
      return {
        ...items,
        total: rowTotal,
        userId,
        firstName,
        secondName,
        isFired,
        isActive,
      } as SalarySummaryRow;
    };

    const resultMap = new Map<number, SalarySummaryRow>();

    users.forEach((u) => {
      const isFiredOrInactive = Boolean(u.isFired) || !u.isActive;
      if (isFiredOrInactive) return;
      const sum = rows?.find((r) => r.userId === u.id);
      resultMap.set(u.id, buildRow(u.id, u.firstName, u.secondName, sum, u.isFired, u.isActive));
    });

    rows?.forEach((r) => {
      if (resultMap.has(r.userId)) return;
      const user = usersMap.get(r.userId);
      resultMap.set(
        r.userId,
        buildRow(r.userId, user?.firstName ?? '', user?.secondName ?? '', r, user?.isFired, user?.isActive),
      );
    });

    return { total, rows: Array.from(resultMap.values()) };
  }, [collection.items, rows]);

  return (
    <GridLayout
      columns={columns}
      items={data.rows}
      isLoading={summaryIsLoading || userIsLoading}
      error={error || userError}
      noItemsText="Список пуст"
      totalItemsCount={data.rows.length}
      newItemRowRender={() => <TotalsRow totals={data.total} rowColor={getRowTotalColor(data.total.total)} />}
      itemRender={(item: SalarySummaryRow) => {
        return (
          <SummaryRow
            key={`SummaryRow as ${item.userId}`}
            summaryItem={item}
            getSpanSize={getSpanSize}
            getPriceColor={getPriceColor}
          />
        );
      }}
    />
  );
};

type RowProps = {
  summaryItem: SalarySummaryRow;
  getSpanSize: (price: number) => 'small' | 'regular';
  getPriceColor: (price: number, type: 'addition' | 'deduction') => string | undefined;
};

const getStatusLabel = (item: SalarySummaryRow): string | null => {
  if (item.isFired) return 'уволен';
  if (!item.isActive) return 'неактивен';
  return null;
};

const SummaryRow = ({ summaryItem, getSpanSize, getPriceColor }: RowProps) => {
  const additions = [
    summaryItem.base_salary,
    summaryItem.commission,
    summaryItem.seek_compensation,
    summaryItem.vacation_compensation,
    summaryItem.bonus,
    summaryItem.other_additions,
    summaryItem.prepayment,
  ];
  const deductions = [summaryItem.taxes, summaryItem.penalty, summaryItem.other_deductions];

  const statusLabel = getStatusLabel(summaryItem);
  const isInactive = statusLabel !== null;

  return (
    <TableRow key={`tableRow as ${summaryItem.userId}`}>
      <TableCell sx={{ p: 1.5 }}>
        <span style={isInactive ? { color: '#9e9e9e' } : undefined}>
          {summaryItem.secondName} {summaryItem.firstName.charAt(0)}.
          {statusLabel && ` (${statusLabel})`}
        </span>
      </TableCell>
      {SUMMARY_COLUMNS.map(({ key, type }) => (
        <TableCell key={key} align="right" sx={{ whiteSpace: 'nowrap' }}>
          {summaryItem[key] < 0 && '−'}
          <PriceSpan size={getSpanSize(summaryItem[key])} price={summaryItem[key]} color={getPriceColor(summaryItem[key], type)} coloring={false} />
        </TableCell>
      ))}
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        {summaryItem.total < 0 && '−'}
        <PriceSpan
          size={getSpanSize(summaryItem.total)}
          price={summaryItem.total}
          coloring={false}
          color={getPriceColor(summaryItem.total, summaryItem.total > 0 ? 'addition' : 'deduction')}
        />
      </TableCell>
    </TableRow>
  );
};